/* eslint-disable no-console */
import { jsPDF } from 'jspdf';

import theme from '../../assets/theme';
import {
  CuripodElement,
  CuripodText,
  parseSlateStringToText,
} from '../../components/SlideComponents/slateUtil';
import {
  Slide,
  SlideElementTextType,
} from '../../components/SlideComponents/slide.types';
import {
  isSlideElementAttachmentPassage,
  isSlideElementIframe,
  isSlideElementImage,
  isSlideElementShape,
  isSlideElementSvg,
  isSlideElementText,
} from '../../components/SlideComponents/slideUtil';
import { Telemetry } from '../../hooks/useTelemetry';
import BackendService, {
  LessonAttachment,
} from '../../services/backendService/backendService';
import { addBackground } from './addBackground';
import { addIframe } from './addIframe';
import { addImage } from './addImage';
import { addPollOptions } from './addPollOptions';
import { addShape } from './addShape';
import { addSvg } from './addSvg';
import {
  nunitoBase64,
  nunitoBoldBase64,
  nunitoBoldItalicBase64,
  nunitoItalicBase64,
} from './nunito.font';
import { resetMathCache } from './renderMath';
import renderTextElement from './renderTextElement';
import { addLogo, getProp, parseJSON, setRotation } from './utils';

const setElementRotation = (
  pdf: jsPDF,
  degrees: unknown,
  pivotX: unknown,
  pivotY: unknown,
) => {
  if (
    typeof degrees !== 'number' ||
    typeof pivotX !== 'number' ||
    typeof pivotY !== 'number'
  )
    return;
  if (degrees % 360 === 0) return;
  setRotation(pdf, degrees, pivotX, pivotY);
};

const exportSlide = async (
  pdf: jsPDF,
  slide: Slide,
  lessonAttachments: LessonAttachment[] | undefined,
) => {
  pdf.addPage([slide.width, slide.height], 'landscape');
  await addBackground(pdf, slide);
  for await (const element of slide.elements) {
    try {
      pdf.setGState(pdf.GState({ opacity: element.opacity }));
      pdf.saveGraphicsState();
      setElementRotation(
        pdf,
        element.angle,
        element.x + element.width / 2,
        element.y + element.height / 2,
      );
      if (isSlideElementText(element)) {
        const elementContent =
          parseJSON(getProp(element as SlideElementTextType, 'textValue')) ?? [];
        if (Array.isArray(elementContent)) {
          await renderTextElement({
            pdf,
            textElements: elementContent,
            x: element.x,
            y: element.y,
            width: element.width,
            scale: element.scale,
          });
        }
      } else if (isSlideElementShape(element)) {
        await addShape(pdf, element);
      } else if (isSlideElementSvg(element)) {
        await addSvg(pdf, element);
      } else if (isSlideElementImage(element)) {
        await addImage(pdf, element);
      } else if (isSlideElementIframe(element)) {
        await addIframe(pdf, element);
      } else if (isSlideElementAttachmentPassage(element)) {
        const passageNum = lessonAttachments?.findIndex(
          attachment => attachment.attachmentId === element.attachmentId,
        );
        if (passageNum !== -1 && passageNum !== undefined) {
          // Draw green box
          pdf.setDrawColor(theme.colors.primary); // green
          pdf.setFillColor(theme.colors.primary); // light green background
          pdf.roundedRect(
            element.x,
            element.y,
            element.width,
            element.height,
            10, // x radius
            10, // y radius
            'F', // fill only
          );

          pdf.setTextColor('#ffffff');
          pdf.setFont('Nunito', 'Bold');
          const fontSizeScaleToElement = element.width / 100;
          pdf.setFontSize(10 * fontSizeScaleToElement);
          const passageTitle = parseSlateStringToText(
            lessonAttachments![passageNum].title,
          );
          const title = `Passage ${passageNum + 1}: ${passageTitle}`;
          const subtitleLine = `Flip to the end to read this passage`;
          const textX = element.x + 5 * fontSizeScaleToElement;
          let textY = element.y + 30 * fontSizeScaleToElement;

          const splitLines = pdf.splitTextToSize(
            title,
            element.width - 10 * fontSizeScaleToElement,
          );
          for (const line of splitLines) {
            pdf.text(line, textX, textY);
            textY += 15 * fontSizeScaleToElement;
          }
          pdf.setFontSize(5 * fontSizeScaleToElement);
          pdf.text(subtitleLine, element.x + 5 * fontSizeScaleToElement, element.y + 34);
        }
      } else {
        console.debug(`Unknown element type - "${element.type}"`);
      }
      pdf.restoreGraphicsState();
    } catch (e) {
      // When an element fails crashes, we just skip that element in the export.
      console.error(e);
      Telemetry()?.noticeError(e);
      return;
    }
  }
  if (slide.type === 'poll' || slide.type === 'multipleChoice') {
    await addPollOptions(pdf, slide);
  }
  await addLogo(pdf, slide.width, false);

  addPageNumberToCurrentPage(pdf, slide.width - 40, slide.height - 30);
};

export const fetchAttachmentContent = async (
  attachment: LessonAttachment,
  isFromDiscover: boolean,
): Promise<CuripodElement[] | undefined> => {
  try {
    const result = await BackendService.instance.gameFiles.fetchAttachmentContent({
      fileId: attachment.fileId,
      isFromDiscover,
    });
    return result;
  } catch (e) {
    console.error('Failed to fetch attachment content', e);
    return undefined;
  }
};

const renderCuripodContentToPdf = async (
  pdf: jsPDF,
  content: CuripodElement[],
  startY: number,
  passageIndex: number,
  margin = 40,
  lineHeight = 30,
): Promise<number> => {
  let y = startY;
  const maxWidth = pdf.internal.pageSize.getWidth() - margin * 2;

  for (const block of content) {
    const align = block.align || 'left';
    const fontSize = 16;

    pdf.setFontSize(fontSize);

    for (const child of block.children) {
      if (!('text' in child)) continue;

      const curipodText = child as CuripodText;

      const fontStyle: 'Normal' | 'Bold' | 'Italic' | 'BoldItalic' =
        curipodText.bold && curipodText.italic
          ? 'BoldItalic'
          : curipodText.bold
          ? 'Bold'
          : curipodText.italic
          ? 'Italic'
          : 'Normal';

      pdf.setFont('Nunito', fontStyle);
      pdf.setTextColor(curipodText.color || '#000000');

      const splitLines = pdf.splitTextToSize(curipodText.text, maxWidth);
      for (const line of splitLines) {
        if (y + lineHeight > pdf.internal.pageSize.getHeight() - margin) {
          addPageNumberToCurrentPage(
            pdf,
            pdf.internal.pageSize.getWidth() - margin,
            pdf.internal.pageSize.getHeight() - margin,
          );
          pdf.addPage();
          await drawPageHeader(pdf, passageIndex, pdf.internal.pageSize.getWidth());

          pdf.setFontSize(16);
          pdf.setTextColor('#000000');
          y = 70;
        }

        pdf.text(line, margin, y, { align, maxWidth });
        y += lineHeight;
      }
    }

    y;
  }

  return y;
};

const drawPageHeader = async (pdf: jsPDF, passageIndex: number, pageWidth: number) => {
  const margin = 40;

  pdf.setFont('Nunito', 'Normal');
  pdf.setFontSize(12);
  pdf.setTextColor('#444');
  pdf.text(`Material ${passageIndex + 1}`, margin, 30);

  await addLogo(pdf, pageWidth, true);
};

const addPageNumberToCurrentPage = (pdf: jsPDF, x: number, y: number) => {
  const pageNumber = pdf.getCurrentPageInfo().pageNumber;

  pdf.setFont('Nunito', 'Normal');
  pdf.setFontSize(10);
  pdf.setTextColor('#666666');
  pdf.text(`Page ${pageNumber}`, x, y, { align: 'right' });
};

const addAttachmentToPdf = async (
  pdf: jsPDF,
  attachment: LessonAttachment,
  content: CuripodElement[],
  passageIndex: number,
) => {
  const A4_WIDTH = 595.28;
  const A4_HEIGHT = 841.89;
  const margin = 40;

  const pageCountBefore = pdf.getNumberOfPages();

  pdf.addPage([A4_WIDTH, A4_HEIGHT], 'portrait');
  await drawPageHeader(pdf, passageIndex, A4_WIDTH);
  pdf.setFontSize(16);
  pdf.setTextColor('#000000');
  let y = 70;

  // Title
  pdf.setFont('Nunito', 'Bold');
  pdf.setFontSize(30);

  const passageTitle = parseSlateStringToText(attachment.title);
  const splitLines = pdf.splitTextToSize(passageTitle, A4_WIDTH - margin * 2);
  for (const line of splitLines) {
    if (y + 30 > A4_HEIGHT - margin) {
      addPageNumberToCurrentPage(pdf, A4_WIDTH - margin, A4_HEIGHT - margin);
      pdf.addPage();
      await drawPageHeader(pdf, passageIndex, A4_WIDTH);

      pdf.setFontSize(16);
      pdf.setTextColor('#000000');
      y = 70;
    }

    pdf.text(line, margin, y);
    y += 40;
  }

  y = await renderCuripodContentToPdf(pdf, content, y, passageIndex);

  // Add page numbers to all new pages added
  const pageCountAfter = pdf.getNumberOfPages();
  for (let i = pageCountBefore + 1; i <= pageCountAfter; i++) {
    pdf.setPage(i);
    addPageNumberToCurrentPage(pdf, A4_WIDTH - margin, A4_HEIGHT - margin);
  }
};
export const exportPdf = async ({
  slides,
  lessonAttachments,
  fileName,
  onSuccess,
}: {
  slides: Slide[];
  lessonAttachments: LessonAttachment[] | undefined;
  fileName: string;
  onSuccess: () => void;
}) => {
  const attachmentContent =
    lessonAttachments && lessonAttachments.length >= 1
      ? await Promise.all(
          lessonAttachments?.map(attachment =>
            fetchAttachmentContent(attachment, false),
          ) || [],
        )
      : undefined;

  resetMathCache();

  const pdf = new jsPDF({
    unit: 'pt',
    compress: true,
  });
  pdf.addFileToVFS('nunito.ttf', nunitoBase64);
  pdf.addFileToVFS('nunitoBold.ttf', nunitoBoldBase64);
  pdf.addFileToVFS('nunitoBoldItalic.ttf', nunitoBoldItalicBase64);
  pdf.addFileToVFS('nunitoItalic.ttf', nunitoItalicBase64);
  pdf.addFont('nunito.ttf', 'Nunito', 'Normal');
  pdf.addFont('nunitoBold.ttf', 'Nunito', 'Bold');
  pdf.addFont('nunitoBoldItalic.ttf', 'Nunito', 'BoldItalic');
  pdf.addFont('nunitoItalic.ttf', 'Nunito', 'Italic');
  pdf.setFont('Nunito', 'Normal');
  pdf.setFontSize(80);

  for await (const slide of slides) {
    await exportSlide(pdf, slide, lessonAttachments);
  }

  if (attachmentContent?.length) {
    for (let index = 0; index < attachmentContent.length; index++) {
      const content = attachmentContent[index];
      const attachment = lessonAttachments?.[index];
      if (content && attachment) {
        await addAttachmentToPdf(pdf, attachment, content, index);
      } else {
        console.error('Attachment content or attachment not found');
      }
    }
  }

  pdf.deletePage(1); // remove default jspdf page

  const safeFileName = /\.pdf$/i.test(fileName) ? fileName : `${fileName}.pdf`;
  await pdf.save(safeFileName, {
    returnPromise: true,
  });
  onSuccess();
};
